import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import { VFC } from "react";

import UserMenu from "components/header/usermenu";
import { Logo, MiniLoader } from "components/utils";

import { useHeader } from "hooks/header/useHeader";
import { useUser } from "hooks/useUser";

import { IoMdNotifications } from "react-icons/io";
import { FiMenu } from "react-icons/fi";

const Search = dynamic(() => import("components/header/search"));
const SideMenu = dynamic(() => import("components/header/sideMenu"), {
  loading: () => <MiniLoader />,
});
const NotifList = dynamic(() => import("components/header/notif"), {
  loading: () => <MiniLoader />,
});

const Header: VFC = () => {
  const { user, isAuthChecking } = useUser();
  const { side, notif, search, openMenu, closeMenu, unReadNotifCount, notifs } =
    useHeader(user);

  return (
    <header
      className="bg-light dark:bg-dark shadow-light-700 dark:shadow-dark-400 shadow-lg
      dark:bg-opacity-90 lg:sticky lg:top-0 lg:z-20 lg:h-[72px] lg:bg-opacity-90"
    >
      <div className="h-full lg:backdrop-blur-sm">
        <div className="container py-4 lg:flex lg:h-full">
          <ul
            className="relative flex 
          items-center gap-x-4 lg:w-full"
          >
            <li className="mr-auto">
              <Link href="/" prefetch={false}>
                <a aria-label="トップページへ行く">
                  {process.env.NEXT_PUBLIC_SITE_NAME}
                </a>
              </Link>
            </li>
            <li className="lg:hidden">
              <button
                onClick={() => openMenu("search")}
                className="nextimg h-7 w-7 brightness-50 grayscale-0 dark:brightness-[3]"
              >
                <Image
                  src="data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%235a5e9a%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Ccircle%20cx%3D%2211%22%20cy%3D%2211%22%20r%3D%228%22%3E%3C%2Fcircle%3E%3Cline%20x1%3D%2221%22%20y1%3D%2221%22%20x2%3D%2216.65%22%20y2%3D%2216.65%22%3E%3C%2Fline%3E%3C%2Fsvg%3E"
                  alt="検索"
                  width="50"
                  height="50"
                />
              </button>
            </li>
            {!isAuthChecking && user && (
              <>
                <li className="relative">
                  <button onClick={() => openMenu("notif")}>
                    <IoMdNotifications className="text-2xl" />
                  </button>
                  {unReadNotifCount > 0 && (
                    <p className="bg-dark text-light dark:bg-light dark:text-dark pointer-events-none absolute top-1/2 left-1/2 flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-full font-bold leading-none">
                      {unReadNotifCount}
                    </p>
                  )}
                </li>
                <li>
                  <button className="nextimg" onClick={() => openMenu("side")}>
                    <Image
                      src={user.avatar || "/default.svg"}
                      alt={user.name || ""}
                      width={40}
                      height={40}
                      className="rounded-full object-cover"
                    />
                  </button>
                </li>
              </>
            )}
            {isAuthChecking && <li className="w-20"></li>}
            {!isAuthChecking && !user && (
              <li>
                <button
                  onClick={() => openMenu("side")}
                  aria-label="メニューを開く"
                >
                  <FiMenu className="text-2xl" />
                </button>
              </li>
            )}
          </ul>
          {isAuthChecking && <div className="w-24" />}
        </div>
      </div>
      <SideMenu {...{ side, closeMenu }}>
        <UserMenu user={user} />
      </SideMenu>
      <Search {...{ search, closeMenu }} />
      <NotifList {...{ notif, notifs, user, closeMenu }} />
    </header>
  );
};

export default Header;
